import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { Text, Modal, modalStyles, theme, type WrappedButtonProps, styles as baseStyles } from '@socrait/react';

import { Period } from '~/services/types';
import { useReviewDeleteMutation } from '~/services/review';
import { showError } from '~/utils/showError';

type DeleteReviewModalProps = {
  period: Period;
  date: Date;
  isOpen: boolean;
  onClose: () => void;
};

export function DeleteReviewModal({ period, date, isOpen, onClose }: DeleteReviewModalProps) {
  const { name } = period ?? {};
  const { mutate: deleteReview } = useReviewDeleteMutation();

  const content = (
    <Text style={modalStyles.text}>
      If you press delete, all collected data for <Text style={[modalStyles.text, baseStyles.boldText]}>{name}</Text> on{' '}
      <Text style={[modalStyles.text, baseStyles.boldText]}>{date.toLocaleDateString()}</Text> will be deleted.{' '}
      <Text style={[modalStyles.text, styles.warning]}>This cannot be undone.</Text>
    </Text>
  );

  const onDeleteReview = useCallback(() => {
    if (!period.id) {
      showError(new Error('Period ID is required'), 'Period ID is required to delete review.');
      onClose();
      return;
    }

    deleteReview(
      { periodId: period.id, date },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          onClose();
          showError(error, 'Failed to delete review. Please try again, or contact support if the issue persists.');
        },
      },
    );
  }, [period.id, date, deleteReview, onClose]);

  const buttons: WrappedButtonProps[] = [
    {
      name: 'Cancel',
      onPress: onClose,
    },
    {
      name: 'Delete',
      icon: 'delete',
      onPress: onDeleteReview,
      variant: 'negative',
    },
  ];

  return (
    <Modal
      title={'Delete class data?'}
      content={content}
      buttons={buttons}
      modalVisible={isOpen}
      handleClose={onClose}
      buttonLayout="row"
    />
  );
}

const styles = StyleSheet.create({
  warning: {
    color: theme.colors.red,
    fontWeight: theme.fontWeights.bold,
  },
});
